import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@lmc-env/environment';
import {
	ArrangementType,
	NlsTransactionType,
	PromiseToPayPaymentMethod,
	PromiseToPayStatus,
	PromiseToPayType,
} from '@lmc-app/shared/enum/promise-to-pay-status.enum';

/** One field's value before and after an edit. */
export interface PromiseToPayFieldChange<T> {
	from: T | null;
	to: T | null;
}

/** The fields an edit changed. Amounts are in cents. */
export interface PromiseToPayChanges {
	promiseDate?: PromiseToPayFieldChange<string>;
	amountInCents?: PromiseToPayFieldChange<number>;
	paymentMethod?: PromiseToPayFieldChange<PromiseToPayPaymentMethod>;
	ownerId?: PromiseToPayFieldChange<number> & {
		fromName: string | null;
		toName: string | null;
	};
}

/**
 * One timeline entry, as the API returns it: a status change, or an edit (no status,
 * with `changes`). `byName` is null when nobody did it — a KapCharge webhook or the
 * broken-promise sweep.
 */
export interface PromiseToPayTimelineEntry {
	at: string;
	status?: PromiseToPayStatus;
	changes?: PromiseToPayChanges;
	by: number | null;
	byName: string | null;
}

/** The KapCharge transaction a submitted promise is collected by. */
export interface LinkedTransaction {
	id: number;
	batchId: string | null;
	status: string | null;
	amountInCents: number;
	dueDate: string | null;
	returnCode: string | null;
}

export interface PromiseToPay {
	id: number;
	acctrefno: number;
	loanNumber: string;
	type: PromiseToPayType;
	arrangementType: ArrangementType | null;
	paymentMethod: PromiseToPayPaymentMethod;
	amountInCents: number;
	/** Dollars, derived server-side from amountInCents. */
	amount: number;
	promiseDate: string;
	status: PromiseToPayStatus;
	comments: string | null;
	transactionId: number | null;
	kapchargeTransaction: LinkedTransaction | null;
	customerName: string | null;
	/**
	 * manual_debit only — whether the loan has an active ACH record, the same check
	 * arranging it would make. null for customer_deposit, which never needs one.
	 */
	hasActiveBankAccount: boolean | null;
	/** Every status change, oldest first. */
	timeline: PromiseToPayTimelineEntry[];
	/** Derived server-side from the timeline's first entry; there is no column for either. */
	createdAt: string | null;
	createdByName: string | null;
	/** The creator until the owner is changed. Null only on unresolvable migrated rows. */
	ownerId: number | null;
	ownerName: string | null;
}

export interface PromiseToPaySearchParams {
	status?: PromiseToPayStatus | null;
	type?: PromiseToPayType | null;
	paymentMethod?: PromiseToPayPaymentMethod | null;
	loanNumber?: string | null;
	acctrefno?: number | null;
	promiseDateFrom?: string | null;
	promiseDateTo?: string | null;
	createdDateFrom?: string | null;
	createdDateTo?: string | null;
	page?: number;
	pageSize?: number;
}

/** One day in the upcoming schedule, or the aggregate beyond it. */
export interface UpcomingBucket {
	count: number;
	amount: number;
}

export interface UpcomingDay extends UpcomingBucket {
	date: string;
}

/**
 * Requested promises still needing to be arranged, by the business day they fall due — one
 * entry per business day in the window (zero or not), plus everything after it as a single
 * total. A promise due on a weekend or an Ontario public holiday is folded into the next
 * business day's entry. Covers the whole filtered set, not the page on screen.
 */
export interface UpcomingSummary {
	days: number;
	buckets: UpcomingDay[];
	later: UpcomingBucket & { from: string };
}

export interface PromiseToPayListResponse {
	rows: PromiseToPay[];
	total: number;
	page: number;
	pageSize: number;
	upcoming: UpcomingSummary;
}

export interface PromiseToPayCreatePayload {
	acctrefno: number;
	type: PromiseToPayType;
	arrangementType?: ArrangementType | null;
	paymentMethod: PromiseToPayPaymentMethod;
	amount: number;
	promiseDate: string;
	comments?: string | null;
}

export interface PromiseToPayUpdatePayload {
	paymentMethod?: PromiseToPayPaymentMethod;
	amount?: number;
	promiseDate?: string;
	comments?: string | null;
	ownerId?: number;
	/** Customer deposits only; see PROMISE_TO_PAY_DEPOSIT_EDIT_STATUSES. */
	status?: PromiseToPayStatus;
	/** manual_debit with no transaction moved to Arranged: raises the KapCharge payment, as arrange() does. */
	transactionType?: NlsTransactionType;
	payoff?: boolean;
	bankAccount?: ArrangeBankAccount;
}

/** An employee a promise may be handed to. */
export interface PromiseToPayOwner {
	employeeID: number;
	fullName: string;
}

/** The current user's Promise to Pay permissions, set in Admin Settings. */
export interface PromiseToPayPermissions {
	canChangeOwner: boolean;
	canExceedCap: boolean;
	canBackdate: boolean;
	/** May edit any promise in any status. */
	canEdit: boolean;
	/** Owner candidates; empty unless canChangeOwner. */
	owners: PromiseToPayOwner[];
}

/** A field-level rejection from create or update. */
export interface PromiseToPayFieldError {
	field: string;
	message: string;
}

/**
 * The choice made in the arrange dialog. `payoff` is Payment-only; sent for any other
 * transactionType, the server ignores it rather than rejecting the request.
 */
export interface ArrangeOptions {
	transactionType?: NlsTransactionType;
	payoff?: boolean;
	/** The account to charge when the loan has no active one; omitted to use the active one. */
	bankAccount?: ArrangeBankAccount;
}

/** A bank account picked in the bank account dialog. */
export interface ArrangeBankAccount {
	institutionCode: string;
	transitNumber: string;
	accountNumber: string;
}

@Injectable({ providedIn: 'root' })
export class PromiseToPayService {
	private readonly uri: string = `${environment.DO_URL}/v2/promise-to-pay`;

	constructor(private readonly http: HttpClient) {}

	list(params: PromiseToPaySearchParams): Observable<PromiseToPayListResponse> {
		let httpParams = new HttpParams();
		Object.entries(params).forEach(([key, value]) => {
			if (value !== null && value !== undefined && value !== '') {
				httpParams = httpParams.set(key, String(value));
			}
		});
		return this.http.get<PromiseToPayListResponse>(this.uri, {
			params: httpParams,
		});
	}

	getPermissions(): Observable<PromiseToPayPermissions> {
		return this.http.get<PromiseToPayPermissions>(`${this.uri}/permissions`);
	}

	/** The loan-meta tab feed: the loan's promises, excluding cancelled and deleted ones. */
	listForLoan(acctrefno: number): Observable<PromiseToPay[]> {
		return this.http.get<PromiseToPay[]>(`${this.uri}/loan/${acctrefno}`);
	}

	create(payload: PromiseToPayCreatePayload): Observable<PromiseToPay> {
		return this.http.post<PromiseToPay>(this.uri, payload);
	}

	update(
		id: number,
		payload: PromiseToPayUpdatePayload
	): Observable<PromiseToPay> {
		return this.http.patch<PromiseToPay>(`${this.uri}/${id}`, payload);
	}

	/**
	 * Arranging a manual_debit promise also raises its KapCharge payment — the server does
	 * both, so there is nothing to confirm here beyond the arrange dialog's own choice of
	 * `transactionType`, and no separate submit call. Neither option is read for a
	 * customer_deposit; pass `{}` for those.
	 */
	arrange(id: number, options: ArrangeOptions = {}): Observable<PromiseToPay> {
		return this.http.post<PromiseToPay>(`${this.uri}/${id}/arrange`, options);
	}

	cancel(id: number): Observable<PromiseToPay> {
		return this.http.post<PromiseToPay>(`${this.uri}/${id}/cancel`, {});
	}

	delete(id: number): Observable<PromiseToPay> {
		return this.http.delete<PromiseToPay>(`${this.uri}/${id}`);
	}

	/** Customer deposits only — an accountant confirming the money arrived. */
	markProcessed(id: number): Observable<PromiseToPay> {
		return this.http.post<PromiseToPay>(`${this.uri}/${id}/mark-processed`, {});
	}

	/** Customer deposits only — the deposit never arrived. */
	markBroken(id: number): Observable<PromiseToPay> {
		return this.http.post<PromiseToPay>(`${this.uri}/${id}/mark-broken`, {});
	}
}
